/**
 * Phase 328 wave 4 — the form MODEL: everything that reads a declared schema and decides what
 * is visible, what a blank answer set looks like, what is valid, and what gets submitted.
 *
 * It lives apart from the components on purpose. The wizard, the flat page and the tests all
 * ask the SAME functions these questions, so "hidden fields are excluded from the payload and
 * from required-validation" is one rule in one place rather than a rule re-implemented per
 * surface. The components render; this module decides.
 *
 * Every function is schema-driven — the schema is the whitelist. Nothing here knows a field
 * name, a form id, or a tenant.
 */
import { formatError, formatHints, isFormatPreset, patternError } from "./formats.js";
import { addressError, asAddress, formatAddress, formatDate, isAddressEmpty, maskSensitive } from "./formDesign.js";
import type {
  AddressValue,
  FieldEvidenceRef,
  FieldOption,
  FieldSpec,
  FieldValue,
  FormRowLedgerColumn,
  FormRowTone,
  FormRowTones,
  FormRowWords,
  FormSchema,
  FormStep,
  FormValues,
  GroupFieldSpec,
  GroupRow,
  ScalarFieldSpec,
  VisibleWhen,
} from "../types.js";
import { CHOICE_FIELD_TYPES, FORM_ROW_TONE_VALUES } from "../types.js";
import {
  CTX_PREFIX,
  FIELDS_PREFIX,
  PARTY_PREFIX,
  isVisible,
  readCtxRow,
  rowScope,
  type PredicateScope,
} from "./predicates.js";

/** The submit endpoint's per-field size guard, mirrored client-side for a friendlier message. */
export const MAX_FIELD_LENGTH = 4096;

/**
 * How a multi-select's selections are joined when it appears as a repeating-group SUBFIELD
 * (a group row's cells are string-valued — bar the one `address` arm {@link GroupRow} admits —
 * so a row keeps the `Array<Record<string, string>>` shape the downstream party readers
 * consume). Top-level multi-selects submit a real `string[]`.
 */
export const GROUP_LIST_SEPARATOR = ", ";

export const REQUIRED_MESSAGE = "This field is required.";
export const EMAIL_MESSAGE = "Enter a full email address, like name@example.co.uk";
export const URL_MESSAGE = "Enter a web address, like www.example.co.uk";
export const TOO_LONG_MESSAGE = "This field is too long.";
/**
 * BB walk 2 — a `read_only` field declared `required` (legal only when a lookup's
 * `choose.populate` fills it, ARD-DSL-036): the subject fills it by PICKING a match, so the
 * message points at the pick rather than at a box that is not there.
 */
export const PICK_REQUIRED_MESSAGE = "Search above and confirm your match before you continue.";

/** Error-map key for a repeating-group subfield: `<group>.<rowIndex>.<subfield>`. */
export function groupErrorKey(group: string, rowIndex: number, subfield: string): string {
  return `${group}.${rowIndex}.${subfield}`;
}

/** Minimum-rows message for a group, derived from its own declaration. */
export function minRowsMessage(min: number): string {
  return `Add at least ${min} ${min === 1 ? "entry" : "entries"}.`;
}

export function isGroupField(field: FieldSpec): field is GroupFieldSpec {
  return field.type === "group";
}

/** A field's display label: the declared one, else its name humanised. */
export function fieldLabel(field: { name: string; label?: string }): string {
  return field.label ?? humanise(field.name);
}

export function humanise(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Visibility ─────────────────────────────────────────────────────────────────────────────

/** The steps a subject can currently reach, in declared order. */
export function visibleSteps(schema: FormSchema, scope: PredicateScope): FormStep[] {
  return (schema.steps ?? []).filter((step) => isVisible(step.visible_when, scope));
}

/**
 * The entries of a declared list that are currently shown. Generic over the spec type so a
 * group's scalar subfield list stays a scalar subfield list — and so the ONE visibility rule
 * also serves the other thing a declaration can make conditional: a step's `cards`
 * (Phase 532). A card is not a field, but "renders iff its `visible_when` holds" is the same
 * sentence, and a second copy of it would be a second thing to keep in step with the grammar.
 */
export function visibleFields<T extends { visible_when?: VisibleWhen }>(
  fields: readonly T[] | undefined,
  scope: PredicateScope,
): T[] {
  return (fields ?? []).filter((field) => isVisible(field.visible_when, scope));
}

/** Does this schema drive a step wizard? Its absence is the legacy flat contract. */
export function isWizard(schema: FormSchema): boolean {
  return (schema.steps ?? []).length > 0;
}

/**
 * Every field the subject can currently see, across the whole form — the flat list for a
 * legacy schema, the visible steps' visible fields for a wizard. This is the ONE definition
 * of "in scope right now" that validation and payload-building both use.
 */
export function visibleFieldsOfForm(schema: FormSchema, scope: PredicateScope): FieldSpec[] {
  if (!isWizard(schema)) return visibleFields(schema.fields, scope);
  return visibleSteps(schema, scope).flatMap((step) => visibleFields(step.fields, scope));
}

/** Every field the schema declares anywhere, visible or not. */
export function declaredFields(schema: FormSchema): FieldSpec[] {
  return [...(schema.fields ?? []), ...(schema.steps ?? []).flatMap((step) => step.fields ?? [])];
}

// ── Values ─────────────────────────────────────────────────────────────────────────────────

/**
 * The blank answer set for a schema: repeating groups start at their declared minimum row
 * count so the subject sees the rows the declaration requires. Scalars stay ABSENT until the
 * subject answers them — that is the Phase-22 payload behaviour (an untouched optional field
 * is simply not submitted) and it must not change for flat forms.
 */
export function seedValues(schema: FormSchema): FormValues {
  const seeded: FormValues = {};
  for (const field of declaredFields(schema)) {
    if (isGroupField(field)) seeded[field.name] = blankRows(field);
  }
  return seeded;
}

/**
 * Phase 328 wave 5 task 5.2 — the answer set a RETURNING subject resumes with: the blank seed
 * of {@link seedValues}, overwritten by whatever the token's server-side draft holds (AC-5).
 * A missing draft returns the blank seed, so both journeys are the same call.
 *
 * The schema is the whitelist here exactly as it is everywhere else in this module. Two things
 * follow, and both matter:
 *  - a draft key the declaration no longer declares is DROPPED rather than carried around in
 *    memory waiting to be submitted;
 *  - every restored value is coerced through the same readers the controls use, so a draft
 *    written against an older shape of the schema (a field that was a `string` and is now a
 *    `checkbox_group`, a group whose rows are junk) resumes as a blank answer instead of
 *    crashing an input on a subject who has done nothing wrong.
 *
 * An unanswered scalar stays ABSENT, preserving the Phase-22 payload behaviour through a
 * resume: restoring a draft must not start submitting empty strings a first-time subject
 * would never have sent.
 */
export function restoreValues(schema: FormSchema, draft: FormValues | null | undefined): FormValues {
  const values = seedValues(schema);
  if (draft === null || typeof draft !== "object" || Array.isArray(draft)) return values;
  const saved = draft as Record<string, unknown>;

  for (const field of declaredFields(schema)) {
    // Phase 532 — the written reason for changing a served pre-answer is not a declared field,
    // but it is the assessor's own typing and it explains an answer that DOES survive a resume.
    // Dropping it would silently re-arm the obligation on a sitting where they already met it.
    if (ownsChangeReason(field) && preAnswer(field) !== undefined) {
      const reason = toRowCell(saved[changeReasonName(field.name)]);
      if (reason.length > 0) values[changeReasonName(field.name)] = reason;
    }

    const value = saved[field.name];
    if (value === undefined || value === null) continue;

    if (isGroupField(field)) {
      const rows = asRows(value as FieldValue).map((row) => restoreRow(field, row));
      // A draft holding fewer rows than the declaration's floor is topped up rather than
      // trusted — `min` is what the subject must be SHOWN, not merely what they must submit.
      while (rows.length < Math.max(0, field.min ?? 0)) rows.push(blankRow(field));
      values[field.name] = rows;
      continue;
    }

    if (field.type === "boolean") { values[field.name] = asBoolean(value as FieldValue); continue; }
    if (field.type === "checkbox_group") { values[field.name] = asStringArray(value as FieldValue); continue; }

    // Phase 541 — an `address` answer is NOT a string on the wire, and falling it through to
    // `toRowCell` below projected it to "" and so dropped it: a top-level address typed,
    // autosaved and resumed came back BLANK. It restores through the same coercion every other
    // address reader uses, and an EMPTY one contributes no key — the "an unanswered scalar
    // stays ABSENT" rule this function is built on, spelled for the shape whose empty is `{}`.
    if (field.type === "address") {
      const address = asAddress(value as FieldValue);
      if (!isAddressEmpty(address)) values[field.name] = address;
      continue;
    }

    // Every other scalar is a string on the wire; `toRowCell` is this module's one rule for
    // projecting an arbitrary value into one. Card #2908 — a draft saved before the select
    // pre-fill rule may hold a value its dropdown cannot show; it resumes blank, not hidden.
    const text = selectPrefill(field, toRowCell(value));
    if (text.length > 0) values[field.name] = text;
  }

  return values;
}

/**
 * One restored group row: the declared subfields only, each projected back to the cell its own
 * TYPE says a row holds — so an `address` subfield resumes as the address it was, rather than
 * as the empty string a string-only projection turned it into.
 */
function restoreRow(group: GroupFieldSpec, row: GroupRow): GroupRow {
  const restored = blankRow(group);
  const cells = row as Record<string, unknown>;
  for (const sub of group.fields ?? []) {
    const projected = toRowCellFor(sub.type, cells[sub.name] as FieldValue | undefined);
    // Card #2908 — the same select rule as a pre-fill: a cell the dropdown cannot show resumes blank.
    const cell = typeof projected === "string" ? selectPrefill(sub, projected) : projected;
    if (!isBlankCell(cell)) restored[sub.name] = cell;
  }
  return restored;
}

/** `min` blank rows (at least none) for a repeating group. */
export function blankRows(group: GroupFieldSpec): GroupRow[] {
  const min = Math.max(0, group.min ?? 0);
  return Array.from({ length: min }, () => blankRow(group));
}

export function blankRow(group: GroupFieldSpec): GroupRow {
  const row: GroupRow = {};
  for (const sub of group.fields ?? []) row[sub.name] = "";
  return row;
}

export function asString(value: FieldValue | undefined): string {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) return "";
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return String(value);
  return "";
}

export function asBoolean(value: FieldValue | undefined): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.trim().length > 0 && value.trim().toLowerCase() !== "false";
  return false;
}

export function asStringArray(value: FieldValue | undefined): string[] {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === "string");
  if (typeof value === "string" && value.length > 0) return [value];
  return [];
}

export function asRows(value: FieldValue | undefined): GroupRow[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is GroupRow => typeof item === "object" && item !== null && !Array.isArray(item));
}

/**
 * How ANY value projects into ONE string. Three surfaces need exactly this rule — a
 * repeating-group cell (rows are string-valued on the wire), a lookup request arg (form
 * fields are strings on the wire), and a text control autofilled from a connector envelope —
 * so it lives here once rather than as three near-copies that drift.
 *
 * A value with no scalar reading (an object, a null, a NaN) projects to the empty string,
 * which every caller reads as "nothing to write".
 */
export function toRowCell(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "true" : "";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (Array.isArray(value)) {
    return value.map(toRowCell).filter((item) => item.length > 0).join(GROUP_LIST_SEPARATOR);
  }
  return "";
}

/**
 * The READ-BACK of {@link toRowCell}: how ONE cell projects into the value its control, its
 * row summary and its review line all expect. `checkbox_group` is the only type whose cell
 * holds a LIST, and it arrives in BOTH of the shapes a multi-select submits — joined on
 * {@link GROUP_LIST_SEPARATOR} as a repeating-group SUBFIELD (rows are string-valued on the
 * wire), or as a real `string[]` at top level. So this is ARRAY-IN / ARRAY-THROUGH:
 *
 *  - already a list ⇒ returned UNTOUCHED. Never re-split (a declared label may itself contain
 *    the separator) and never re-joined.
 *  - a `string` on a `checkbox_group` ⇒ split on the separator; empty or blank ⇒ `[]`.
 *  - anything else on a `checkbox_group` (absent, or a shape a row cell cannot hold) ⇒ `[]`.
 *  - every other type ⇒ the cell as the SINGLE value it already is (absent ⇒ `""`), so a
 *    `select` / `radio` cell containing a comma is never split.
 *
 * It lives here, once, because the three surfaces that read a cell — the group's own
 * controls, the row-card template and the review summary — must agree on what a cell MEANS.
 */
export function fromRowCell(type: string, cell: FieldValue | undefined): FieldValue {
  if (Array.isArray(cell)) return cell;
  if (type === "checkbox_group") {
    if (typeof cell !== "string" || cell.trim().length === 0) return [];
    return cell.split(GROUP_LIST_SEPARATOR);
  }
  return cell ?? "";
}

/**
 * The WRITE twin of {@link fromRowCell}: how one answer becomes the CELL its subfield's type
 * says a row holds. Type-aware for exactly one type, because exactly one type's cell is not a
 * string ({@link GroupRow}): an `address` subfield's cell is the nested {@link AddressValue}
 * object a top-level `address` field already submits, read through the same {@link asAddress}
 * coercion every other address reader uses. Every other type projects through
 * {@link toRowCell}, byte for byte as before.
 *
 * It is a SEPARATE function rather than a widening of `toRowCell` on purpose. `toRowCell` is
 * the "any value as ONE string" rule and most of its call sites are not row cells at all — a
 * lookup request arg, a control's autofill, a change reason, a display line — and every one of
 * them requires a string out. Teaching it to return an object would break each of those
 * silently; this twin is reached only where a value is genuinely becoming a row cell and the
 * subfield's declared type is therefore in hand.
 */
export function toRowCellFor(type: string, value: FieldValue | undefined): string | AddressValue {
  if (type === "address") return asAddress(value);
  return toRowCell(value);
}

/**
 * Is there NOTHING in this cell? The one definition, because a row cell's emptiness is now two
 * sentences rather than one — `""` for a string cell, and "no part filled in" for the
 * {@link AddressValue} an `address` subfield holds, whose `{}` has a perfectly good `length`
 * of `undefined` and would read as non-blank under the `(cell ?? "").length === 0` test every
 * blank/identity read used to spell for itself.
 *
 * Deliberately NOT trimmed, exactly as those reads were not: a cell holding a space was
 * non-blank before this function existed and still is. `isAddressEmpty` makes the opposite
 * choice for an address's parts, and that is ITS rule, unchanged here.
 */
export function isBlankCell(cell: string | AddressValue | undefined): boolean {
  if (cell === undefined || cell === null) return true;
  if (typeof cell === "string") return cell.length === 0;
  return isAddressEmpty(asAddress(cell));
}

// ── Payload ────────────────────────────────────────────────────────────────────────────────

/**
 * The submitted payload: the subject's answers, MINUS anything a predicate currently hides
 * and minus anything the schema does not declare.
 *
 * Built by filtering the answer set rather than by walking the schema, so an untouched
 * optional field stays absent exactly as it does today — the flat single-page payload
 * contract is unchanged. Hidden answers survive in memory (re-showing a step restores what
 * was typed) but never reach the wire.
 *
 * ONE EXCEPTION, and it is why this is not purely a filter (Phase 515): a repeating group's
 * value is not opaque any more. A subfield may carry a `row.`-rooted `visible_when`, which is
 * answered by its OWN row, so each row is re-projected through {@link projectGroupRow} and a
 * subfield hidden for THAT row is dropped from THAT row — per row independently. Everything
 * else about the group value is untouched: see that function for the byte-identity property
 * this exception is bounded by.
 *
 * Phase 532 adds ONE key that is not a declared field: the `<field>_change_reason` of a
 * pre-answer the assessor moved away from ({@link CHANGE_REASON_SUFFIX}). It ships only while
 * that field is BOTH visible and actually changed, so a reason typed and then reverted never
 * reaches the wire as an explanation for an answer nobody changed.
 */
export function buildPayload(schema: FormSchema, values: FormValues, scope: PredicateScope): FormValues {
  const visible = visibleFieldsOfForm(schema, scope);
  const submittable = new Map(visible.map((field) => [field.name, field]));
  /**
   * Phase 532's change-reason keys are submittable but are NOT declared fields, so they ride a
   * SEPARATE set rather than a null entry in the map above: they ship verbatim, and the group
   * re-projection below is only ever reached through a real {@link FieldSpec}.
   */
  const changeReasons = new Set<string>();
  for (const field of visible) {
    if (ownsChangeReason(field) && changedFromPreAnswer(field, values)) {
      changeReasons.add(changeReasonName(field.name));
    }
  }
  const payload: FormValues = {};
  for (const [name, value] of Object.entries(values)) {
    const declared = submittable.get(name);
    if (declared === undefined) {
      if (changeReasons.has(name)) payload[name] = value;
      continue;
    }
    payload[name] = isGroupField(declared) ? projectGroupValue(declared, value, scope) : value;
  }
  // Card #2878 — a HIDDEN field always rides the payload, as "" when nothing filled it (an optional
  // pick left empty): it is the form's own carrier for an id a later step reads, and a workflow's
  // `${ctx.<form>.<field>}` binds strictly, so its key must be there whether or not a pick ran.
  for (const field of visible) {
    if (!isGroupField(field) && field.hidden === true && !(field.name in payload)) payload[field.name] = "";
  }
  return payload;
}

/**
 * A group's submitted value, each row projected against ITSELF.
 *
 * BYTE-IDENTITY, deliberately: a group whose subfields declare no `visible_when` comes out of
 * here as the SAME array object it went in as — same rows, same keys, same key order,
 * including keys the schema does not declare and untouched-optional keys. The projection is a
 * no-op when every subfield is visible, so the group payload only ever changes for a
 * declaration that asked it to. A value that is not an array of rows (a hand-edited draft, an
 * older shape) passes through untouched rather than being coerced on the way to the wire.
 */
function projectGroupValue(group: GroupFieldSpec, value: FieldValue, scope: PredicateScope): FieldValue {
  if (!Array.isArray(value)) return value;
  let changed = false;
  const projected = (value as unknown[]).map((row) => {
    if (typeof row !== "object" || row === null || Array.isArray(row)) return row;
    const next = projectGroupRow(group, row as GroupRow, scope);
    if (next !== row) changed = true;
    return next;
  });
  return changed ? (projected as FieldValue) : value;
}

/**
 * One row, minus the DECLARED subfields its own row currently hides.
 *
 * Only a declared-and-hidden key is dropped. An undeclared key is left exactly where it is —
 * this function's job is the `row.` rule, not the schema-whitelist rule, and the group's value
 * has never been schema-filtered on the way out. Nothing hidden means the ORIGINAL row object
 * is returned, which is what makes the no-op case identical rather than merely equal.
 */
function projectGroupRow(group: GroupFieldSpec, row: GroupRow, scope: PredicateScope): GroupRow {
  const declared = group.fields ?? [];
  if (declared.length === 0) return row;
  // Walk 3 (ARD-DSL-070) — a LOCKED row is a set of facts the subject could not touch: every
  // cell, hidden-by-visible_when ones included, goes to the wire exactly as it arrived.
  if (isLockedRow(group, row, scope)) return row;
  const visible = new Set(visibleFields(group.fields, rowScope(scope, row)).map((sub) => sub.name));
  const hidden = new Set(declared.map((sub) => sub.name).filter((name) => !visible.has(name)));
  if (hidden.size === 0) return row;

  const next: GroupRow = {};
  let dropped = false;
  for (const [key, cell] of Object.entries(row)) {
    if (hidden.has(key)) { dropped = true; continue; }
    next[key] = cell;
  }
  return dropped ? next : row;
}

// ── Validation ─────────────────────────────────────────────────────────────────────────────

export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Validates one field list against the current answers. Only VISIBLE fields are checked — a
 * hidden required field can never block a submission it is not part of.
 */
export function validateFields(
  fields: FieldSpec[] | undefined,
  values: FormValues,
  scope: PredicateScope,
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of visibleFields(fields, scope)) {
    if (isGroupField(field)) {
      Object.assign(errors, validateGroup(field, values, scope));
      continue;
    }
    const error = validateScalar(field, values[field.name]);
    if (error) errors[field.name] = error;
    // Phase 532 — the obligation that comes with a served pre-answer, keyed on the reason
    // control rather than on the field: the ANSWER is valid, it is the missing explanation
    // that blocks, and the two must be able to red independently.
    const reasonError = changeReasonError(field, values);
    if (reasonError) errors[changeReasonName(field.name)] = reasonError;
  }

  return errors;
}

/** Validates every visible field of the whole form (all visible steps, or the flat list). */
export function validateForm(
  schema: FormSchema,
  values: FormValues,
  scope: PredicateScope,
): Record<string, string> {
  if (!isWizard(schema)) return validateFields(schema.fields, values, scope);
  const errors: Record<string, string> = {};
  for (const step of visibleSteps(schema, scope)) {
    Object.assign(errors, validateFields(step.fields, values, scope));
  }
  return errors;
}

/**
 * Walk 3 (ARD-DSL-070) — is this row one of the group's LOCKED rows? `false` for a group with no
 * `locked_rows` (opt-in by presence) — never the "absent predicate is visible" default.
 */
export function isLockedRow(group: GroupFieldSpec, row: GroupRow, scope: PredicateScope): boolean {
  const locked = group.locked_rows;
  if (!locked || locked.when === undefined || locked.when === null) return false;
  return isVisible(locked.when, rowScope(scope, row));
}

/** One fact line of a locked row, as the subject reads it. */
export interface LockedRowFact {
  readonly name: string;
  readonly label: string;
  readonly display: string;
}

/**
 * Walk 3 (ARD-DSL-070) — a locked row's `facts`, in declared order, each read back the way the
 * review step reads an answer (option LABELS for choices). Blank facts and undeclared names are
 * omitted.
 */
export function lockedRowFacts(group: GroupFieldSpec, row: GroupRow): LockedRowFact[] {
  const facts: LockedRowFact[] = [];
  for (const name of group.locked_rows?.facts ?? []) {
    const sub = (group.fields ?? []).find((f) => f.name === name);
    if (!sub || (sub.type as string) === "group") continue;
    const display = scalarDisplay(sub, row);
    if (display === NOT_ANSWERED) continue;
    facts.push({ name, label: fieldLabel(sub), display });
  }
  return facts;
}

function validateGroup(
  group: GroupFieldSpec,
  values: FormValues,
  scope: PredicateScope,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const rows = asRows(values[group.name]);
  const min = Math.max(0, group.min ?? 0);

  if (rows.length < min) errors[group.name] = minRowsMessage(min);

  rows.forEach((row, index) => {
    // Walk 3 (ARD-DSL-070) — a locked row has no inputs, so nothing in it can be an error the
    // subject could clear: it is complete by definition and never blocks Next or Submit.
    if (isLockedRow(group, row, scope)) return;
    // Each row judges its own subfields: a `row.`-gated subfield hidden for THIS row can no
    // more block the submission than a hidden top-level field can (Phase 515).
    for (const sub of visibleFields(group.fields, rowScope(scope, row))) {
      // Nested groups are rejected at deploy time (ARD-DSL-036); skipped here rather than
      // trusted, because a hand-edited declaration is still a declaration.
      if ((sub.type as string) === "group") continue;
      const error = validateScalar(sub, row[sub.name]);
      if (error) errors[groupErrorKey(group.name, index, sub.name)] = error;
    }
  });

  return errors;
}

/**
 * The Phase-22 scalar rules, extended to the types whose "empty" is not an empty string.
 *
 * Phase 572 wave 6 task 6.2 — a declared `read_only` field is NEVER validated as an input: it
 * has no control, so nothing the subject could do would clear an error on it, and a hidden
 * (valueless) one must never block next/submit. Guarded here, the ONE scalar rule, so every
 * caller — step validation, whole-form validation, a group row's Done/needed marker — agrees.
 */
export function validateScalar(field: ScalarFieldSpec, value: FieldValue | undefined): string | undefined {
  // BB walk 2 — the ONE exception: a read_only field the declaration marks `required` is filled
  // by the subject's own pick (ARD-DSL-036 admits it only under a `choose.populate`), so while
  // it is still empty the step is not done. Every other read_only field stays unvalidated.
  if (field.read_only === true) {
    return field.required === true && asString(value).trim() === "" ? PICK_REQUIRED_MESSAGE : undefined;
  }
  if (field.type === "address") {
    return addressError(asAddress(value), field.required ?? false);
  }
  if (field.type === "boolean") {
    return field.required && !asBoolean(value) ? REQUIRED_MESSAGE : undefined;
  }

  if (field.type === "checkbox_group") {
    const selections = asStringArray(value);
    if (field.required && selections.length === 0) return REQUIRED_MESSAGE;
    if (selections.some((item) => item.length > MAX_FIELD_LENGTH)) return TOO_LONG_MESSAGE;
    return undefined;
  }

  const text = asString(value);
  if (field.required && !text.trim()) return REQUIRED_MESSAGE;
  if (field.type === "email" && text.trim() && !isEmail(text.trim())) return EMAIL_MESSAGE;
  if (text.length > MAX_FIELD_LENGTH) return TOO_LONG_MESSAGE;
  if (field.type === "url" && text.trim() && !/^(https?:\/\/)?[^\s/$.?#].[^\s]*$/i.test(text.trim())) return URL_MESSAGE;
  // KYC-design — identifier presets and custom patterns, checked on blur and on Continue.
  if (field.type === "string" && isFormatPreset(field.format)) {
    const error = formatError(field.format, text);
    if (error) return error;
  }
  if (typeof field.pattern === "string" && field.pattern.trim().length > 0) {
    const error = patternError(field.pattern, field.message, text);
    if (error) return error;
  }
  return undefined;
}

/**
 * Phase 328 wave 5 task 5.2 — the step a RESUMING subject should land on (AC-5): the first
 * visible step that is not yet complete, judged by the very validation the wizard blocks on,
 * so "incomplete" can never drift from "cannot advance past".
 *
 * When every visible step is already answered the LAST one is returned, not the first: a
 * subject who filled the whole form in and came back for one more look wants the submit
 * button, not page one. Content-only steps carry no fields and so are never landed on — the
 * introduction is read once.
 *
 * Returns 0 for a form with no visible steps, so a caller can use it unconditionally.
 */
export function firstIncompleteStep(
  schema: FormSchema,
  values: FormValues,
  scope: PredicateScope,
): number {
  const steps = visibleSteps(schema, scope);
  if (steps.length === 0) return 0;
  for (let index = 0; index < steps.length; index += 1) {
    if (Object.keys(validateFields(steps[index]!.fields, values, scope)).length > 0) return index;
  }
  return steps.length - 1;
}

/**
 * The index of the first visible step carrying one of `errors` — so a submit blocked by an
 * answer three steps back lands the subject on the step that needs them.
 */
export function firstStepWithError(
  schema: FormSchema,
  errors: Record<string, string>,
  scope: PredicateScope,
): number {
  const keys = Object.keys(errors);
  if (keys.length === 0) return -1;
  const steps = visibleSteps(schema, scope);
  for (let index = 0; index < steps.length; index += 1) {
    const fields = visibleFields(steps[index]!.fields, scope);
    const names = fields.map((field) => field.name);
    // A change-reason error is keyed on the reason control, which is not a declared field —
    // so the step it belongs to has to be derived from the field that owns it, or a submit
    // blocked three steps back would leave the assessor on the last step with no visible cause.
    const reasons = fields
      .filter((field) => ownsChangeReason(field) && preAnswer(field) !== undefined)
      .map((field) => changeReasonName(field.name));
    if (keys.some((key) =>
      names.includes(key) || reasons.includes(key) || names.some((name) => key.startsWith(`${name}.`))
    )) {
      return index;
    }
  }
  return -1;
}

// ── Phase 449 — choice labels ──────────────────────────────────────────────────────────────

/** One choice, projected for rendering: the wire token and the copy the subject reads. */
export interface OptionEntry {
  /** The payload value AND the `visible_when` `eq`/`in` comparand. */
  value: string;
  /** The declared label, else the value humanised. */
  label: string;
}

/**
 * THE projection of a declared `options` list. Every choice control renders these entries and
 * nothing else, so "a bare string is the label-less form of `{value}`" is one rule in one
 * place rather than three near-copies across `select` / `radio` / `checkbox_group`.
 *
 * The VALUE is what reaches the payload and what a predicate compares against; the LABEL never
 * leaves the DOM. An undeclared or empty list projects to no entries, and — as everywhere else
 * in this module — an entry that survived no deploy-time gate (a hand-edited declaration is
 * still a declaration) is SKIPPED rather than trusted into a control with a blank value.
 */
export function optionEntries(field: {
  options?: ReadonlyArray<string | FieldOption>;
}): OptionEntry[] {
  const entries: OptionEntry[] = [];
  for (const option of field.options ?? []) {
    const value = typeof option === "string" ? option : optionValue(option);
    if (value.length === 0) continue;
    const declared = typeof option === "string" ? undefined : option.label;
    entries.push({ value, label: declared && declared.trim() ? declared : humanise(value) });
  }
  return entries;
}

/**
 * Card #2908 — THE select pre-fill rule, one function for every way a `select` answer arrives
 * already filled in: a lookup's `populate` / `choose.populate`, a `populate_group` row cell, a
 * `default_from` carry-forward, and a draft restore.
 *
 * A pre-filled value is only an answer if the dropdown can SHOW it. A company register gives
 * titles like "The Hon" / "The Honourable" for its people; written straight into a person-row
 * Title select (Mr / Mrs / Ms / Mx / Miss / Dr) they were a
 * hidden value — no option or card selected, yet the cell non-blank, so the REQUIRED check passed
 * on an answer nobody could see.
 *
 * So: a value that matches an option's value or label ignoring case, surrounding and repeated
 * inner spacing and one trailing dot ("MR", "Mr.", " mrs ") takes THAT OPTION'S VALUE; anything
 * else ("The Hon", "The Honourable", "Sir", "Lord") becomes `""`, so the required field asks the
 * person. No option is ever invented. Non-select fields, and a select that declares no options
 * (a role-locked fact served without its list), pass through untouched.
 */
export function selectPrefill(
  field: { type: string; options?: ReadonlyArray<string | FieldOption> },
  text: string,
): string {
  if (field.type !== "select") return text;
  const entries = optionEntries(field);
  if (entries.length === 0) return text;
  const wanted = selectMatchKey(text);
  if (wanted.length === 0) return "";
  const hit = entries.find((entry) => selectMatchKey(entry.value) === wanted || selectMatchKey(entry.label) === wanted);
  return hit?.value ?? "";
}

/** Casefolded, trimmed, inner whitespace runs collapsed, one trailing dot dropped. */
function selectMatchKey(text: string): string {
  return text.trim().replace(/\s+/g, " ").replace(/\.$/, "").trim().toLowerCase();
}

/**
 * Walk 2 — how a LOCKED choice field's value reads when the served field carries no options to
 * label it (a role-locked decision field is served as a fact with NO options, so a bare `medium`
 * had nothing to become). A value shaped like a token — lower-case letters, digits, underscores —
 * reads the way a bare-string option's label does ({@link humanise}: `medium` → `Medium`); any
 * other text is already prose and is shown as served. Never the raw token (L-97).
 */
export function lockedTokenLabel(value: string): string {
  return /^[a-z0-9_]+$/.test(value) ? humanise(value) : value;
}

function optionValue(option: FieldOption): string {
  return option !== null && typeof option === "object" && typeof option.value === "string"
    ? option.value
    : "";
}

/** The label a given answer displays as, or the raw text when the list does not declare it. */
function optionLabel(field: ScalarFieldSpec, value: string): string {
  return optionEntries(field).find((entry) => entry.value === value)?.label ?? value;
}

// ── Phase 449 — declarative carry-forward ──────────────────────────────────────────────────

/**
 * The values a field list's `default_from` declarations would contribute right now — the
 * PATCH, not the application of it. Pure: the caller (the step-entry effect) decides when to
 * merge it, and what it merges flows through the same `setValues` every other answer does, so
 * a default autosaves and submits as an ORDINARY ANSWER with no special case downstream.
 *
 * A default is emitted only when the target's key is ABSENT from the answer set — this
 * module's existing "unanswered" invariant ({@link seedValues}). A field the subject CLEARED
 * holds `""`: present, therefore never overwritten, which is what keeps carry-forward from
 * fighting a subject who deliberately emptied a box.
 *
 * Hidden fields contribute nothing (a field a predicate hides is out of scope everywhere in
 * this module), a group and its subfields can carry no default (a repeated row has no single
 * source row — rejected at deploy time, skipped here), and a blank source contributes nothing
 * rather than a blank answer.
 *
 * Phase 515 adds the SECOND root, `party.<key>` — the whitelisted party snapshot on the scope.
 * Every invariant above holds for it UNCHANGED: a party prefill is an ORDINARY ANSWER the
 * subject can edit, clear or retype, it is never re-submitted verbatim and nothing it fills is
 * read-only, and it is applied only where the key is ABSENT from the answer set — so a subject
 * who corrected what the ledger held keeps their correction across a step revisit, an autosave
 * and a resume.
 *
 * Phase 549 adds the THIRD, `ctx.<dotted.path>` — the SERVED ctx snapshot on the scope, whose
 * keys are the verbatim declared paths ({@link defaultValue}). Every invariant above holds for it
 * UNCHANGED too: it is an ordinary editable answer, applied only where the key is absent, and a
 * path the snapshot does not carry contributes nothing rather than a blank answer. It is NOT
 * Phase 532's `from_ctx` pre-answer, which the server resolves and attaches an obligation to;
 * this is the Phase 449 carry-forward with one more place to read from.
 */
export function defaultsPatch(
  fields: FieldSpec[] | undefined,
  values: FormValues,
  scope: PredicateScope,
): FormValues {
  const patch: FormValues = {};

  for (const field of visibleFields(fields, scope)) {
    if (isGroupField(field)) continue;
    const source = defaultSource(field.default_from);
    if (source === undefined) continue;
    if (Object.prototype.hasOwnProperty.call(values, field.name)) continue;

    // The ROOT decides the namespace, and no root ever falls back to another — see
    // {@link defaultValue}. An unresolvable root contributes nothing.
    const raw: unknown = defaultValue(source, values, scope);

    // A source value that is not a scalar (an `address` OBJECT, a ctx record, say) projects to ""
    // and so contributes nothing — a scalar control cannot hold it, and a blank default is a no-op.
    // Card #2908 — a `select` default takes only a value its dropdown can show.
    const text = selectPrefill(field, toRowCell(raw));
    if (text.trim().length === 0) continue;
    patch[field.name] = text;
  }

  return patch;
}

/**
 * A resolved `default_from` source: WHICH namespace to read, and the key to read from it.
 * Discriminated on purpose — a BARE name would collapse the roots into one lookup, so
 * `default_from: party.name` would silently resolve against a top-level form field literally
 * called `name` (and a KYC party row's keys — `name`, `email`, `dob` — are exactly the names a
 * KYC form's own fields carry, so that collision is the common case, not the exotic one). The
 * `ctx.` root makes the same point in the other direction: an ANSWER whose key is literally
 * spelled `ctx.client.email` is not the ctx snapshot's `ctx.client.email`.
 *
 * What a `key` MEANS differs by root, because the namespaces are keyed differently:
 *  - `fields` / `party` — the BARE one-segment name, prefix stripped (an answer key; a party
 *    snapshot key, which `BuildPartySnapshot` emits bare);
 *  - `ctx` — the WHOLE declared string, prefix INCLUDED and dotted, because the served ctx
 *    snapshot is keyed by the verbatim declared path (`FormsApi.BuildContextSnapshot`, and
 *    `resolvePath`'s ctx arm reads it the same way).
 */
interface DefaultSource {
  readonly root: "fields" | "party" | "ctx";
  readonly key: string;
}

/**
 * The source a `default_from` names, or undefined when it names nothing this renderer can
 * resolve. THREE roots, the same set the deploy-time grammar admits (ARD-DSL-036):
 * `fields.<declared top-level field>` — the SAME root a predicate leaf addresses, hence the
 * shared {@link FIELDS_PREFIX} — `party.<key>`, a key of the whitelisted party snapshot
 * ({@link PARTY_PREFIX}), and (Phase 549) `ctx.<dotted.path>` ({@link CTX_PREFIX}), a path of
 * the served ctx snapshot — which now carries every path a `default_from` names, not only the
 * ones a `visible_when` does, so a declared ctx default can no longer resolve to nothing on a
 * form that does not also branch on it.
 *
 * Any other root resolves to nothing, as does a multi-segment key under `fields.` / `party.` and
 * a bare `ctx.` with no path after it. Segment SHAPE is not re-validated here: deploy time owns
 * the grammar, and a key the snapshot does not carry already contributes nothing.
 */
function defaultSource(declared: string | undefined): DefaultSource | undefined {
  if (typeof declared !== "string") return undefined;
  if (declared.startsWith(CTX_PREFIX)) {
    // VERBATIM: the snapshot's key is the whole path, so nothing is sliced off. One or more
    // segments — a ctx path is dotted, unlike the other two roots' bare names.
    return declared.length > CTX_PREFIX.length ? { root: "ctx", key: declared } : undefined;
  }
  for (const [prefix, root] of [[FIELDS_PREFIX, "fields"], [PARTY_PREFIX, "party"]] as const) {
    if (!declared.startsWith(prefix)) continue;
    const key = declared.slice(prefix.length);
    return key.length > 0 && !key.includes(".") ? { root, key } : undefined;
  }
  return undefined;
}

/**
 * The value behind a resolved source, read from THAT ROOT'S OWN NAMESPACE and no other.
 *
 * THE NAMESPACE NEVER COLLAPSES. A `party.name` default reads the party snapshot even when a
 * top-level field called `name` is sitting right there answered; a `fields.name` default reads
 * the answer set even when the party row carries a `name`; and a `ctx.client.email` default
 * reads the ctx snapshot even when an ANSWER is literally keyed `ctx.client.email` — which is a
 * real shape, because an answer set is keyed by declared field name and nothing forbids that
 * spelling. Each arm reads one object, and none of them falls through to another.
 *
 * The ctx arm is the SAME own-property lookup `resolvePath`'s ctx arm makes (`predicates.ts`):
 * one direct read of the verbatim key, no traversal, so a dot inside a segment can never be
 * misread and a path the snapshot does not carry is simply `undefined` = "no default".
 */
function defaultValue(source: DefaultSource, values: FormValues, scope: PredicateScope): unknown {
  if (source.root === "fields") return values[source.key];
  if (source.root === "party") return scope.party[source.key];
  return Object.prototype.hasOwnProperty.call(scope.ctx, source.key) ? scope.ctx[source.key] : undefined;
}

// ── Phase 532 — the served pre-answer (ARD-DSL-051) ────────────────────────────────────────
//
// The THIRD way an answer can arrive already filled in, and the only one the engine decides:
//   `default_from`        — carried forward from another FIELD of this form (Phase 449).
//   a lookup's `populate` — filled by a connector the BROWSER called on a trigger field.
//   `from_ctx`            — resolved SERVER-SIDE from the job's own ctx, and served on the
//                           field as `from_ctx_value` + its label, reason and evidence.
//
// The three coexist and none reads another's state: this section reads nothing but the served
// keys and the answer set, exactly as `defaultsPatch` reads nothing but `default_from` and the
// answer set. What makes `from_ctx` different from the other two is the OBLIGATION: the file
// gave an answer, so moving away from it is a thing a person has to explain in writing. That
// obligation is enforced for real on the server (it re-resolves and refuses a bare change with
// `change_reason_required`); everything here is the client half of the same rule, so the
// assessor meets it at the control rather than at a 400.

/**
 * The per-field suffix carrying the written reason for changing a served pre-answer —
 * the wire twin of `FormCtxPreAnswerResolver.ChangeReasonSuffix`. The reason rides the SAME
 * answer object as the answer it explains, so the two are submitted, saved and rolled back
 * together.
 */
export const CHANGE_REASON_SUFFIX = "_change_reason";

/** The blocking message a changed pre-answer with no written reason shows. */
export const CHANGE_REASON_MESSAGE = "Say why you are changing this answer.";

/** The answer-set key (and control id) carrying `field`'s change reason. */
export function changeReasonName(field: string): string {
  return `${field}${CHANGE_REASON_SUFFIX}`;
}

/**
 * Does this field's written reason ride the {@link CHANGE_REASON_SUFFIX} CONVENTION?
 *
 * ONLY a field that DECLARES `from_ctx` does, because the convention is one half of a rule whose
 * other half is server-side: `FormCtxPreAnswerResolver.Reconcile` re-resolves exactly the
 * `from_ctx` fields and refuses a bare change with `change_reason_required`, keyed on
 * `<name>_change_reason`. Demanding that key anywhere else would be the client inventing an
 * obligation no door enforces — and, worse, one with no control to meet it at.
 *
 * <p>A field can carry a SERVED pre-answer without declaring `from_ctx`: since Phase 532 the
 * staff approval form's decision step is generated from the bound gate's own `decision_fields`
 * (`ApprovalFormBinding.BuildField`), and a field declaring `computed_from` is served with the
 * engine's computed value in the SAME `from_ctx_value` channel — which is the point, because the
 * pre-answer, the source chip and the "changed" state are then one implementation, not two. What
 * differs is only where the reason is WRITTEN: that gate declares its own reason FIELD
 * (`required_when_differs_from`), served with the reveal predicate that shows it the moment the
 * approver moves off the computed value. One interaction, two declarations of it.</p>
 */
export function ownsChangeReason(field: FieldSpec): boolean {
  return !isGroupField(field) && (field as ScalarFieldSpec).from_ctx !== undefined;
}

/**
 * The pre-answer the server served for this field, or `undefined` when it served none.
 *
 * A null / blank `from_ctx_value` is deliberately the SAME answer as an absent one: the
 * resolver's whole posture is that "we could not tell" never becomes a selected option, so a
 * field with no pre-answer has no pre-answer to keep, change or explain.
 */
export function preAnswer(field: FieldSpec): string | undefined {
  if (isGroupField(field)) return undefined;
  const served = (field as ScalarFieldSpec).from_ctx_value;
  return typeof served === "string" && served.length > 0 ? served : undefined;
}

/**
 * Has the answer on the row moved away from the pre-answer the server served?
 *
 * BLANK IS NOT A CHANGE — the same line the server draws (`FormCtxPreAnswerResolver.IsAnswered`).
 * An emptied choice is an UNANSWERED question, and the field's own `required` rule is what has an
 * opinion about that; demanding a written reason for a blank would block a submit the server
 * would have accepted, which is the one way a client-side mirror of a server rule can do harm.
 */
export function changedFromPreAnswer(field: FieldSpec, values: FormValues): boolean {
  const served = preAnswer(field);
  if (served === undefined) return false;
  const answered = asString(values[field.name]).trim();
  return answered.length > 0 && answered !== served;
}

/**
 * The reason error for one field, or `undefined` when none is owed — the client half of the
 * server's `change_reason_required`. Whitespace is not a reason, for the same reason it is not
 * one on the server: the point of the obligation is that a human wrote down why they disagreed
 * with the file.
 */
export function changeReasonError(field: FieldSpec, values: FormValues): string | undefined {
  // The obligation follows the CONVENTION, not the pre-answer: a served decision field carries a
  // pre-answer too, and its reason is a declared field of its own ({@link ownsChangeReason}).
  if (!ownsChangeReason(field)) return undefined;
  if (!changedFromPreAnswer(field, values)) return undefined;
  const written = asString(values[changeReasonName(field.name)]);
  if (written.trim().length === 0) return CHANGE_REASON_MESSAGE;
  return written.length > MAX_FIELD_LENGTH ? TOO_LONG_MESSAGE : undefined;
}

/**
 * The patch a served pre-answer contributes — `{field: served value}` for every pre-answered
 * field whose key is ABSENT from the answer set. The caller decides when to merge it, and what
 * it merges becomes an ORDINARY ANSWER: it autosaves, it validates and it submits like anything
 * the assessor typed, with no special case downstream.
 *
 * "Absent" is this module's existing unanswered invariant ({@link seedValues}), which is what
 * makes a RESUME safe: a draft the assessor has already touched holds the key, so the pre-answer
 * never overwrites a decision they already made — including one they made by agreeing with it.
 *
 * Visibility is deliberately NOT consulted. The server resolves a pre-answer per FIELD, and a
 * predicate that hides the field is evaluated against the very answers this patch supplies;
 * filtering here would make the result depend on the order the two were applied in. A hidden
 * answer reaches neither the payload ({@link buildPayload}) nor validation, so carrying it costs
 * nothing.
 */
export function preAnswerPatch(fields: readonly FieldSpec[], values: FormValues): FormValues {
  const patch: FormValues = {};
  for (const field of fields) {
    const served = preAnswer(field);
    if (served === undefined) continue;
    if (Object.prototype.hasOwnProperty.call(values, field.name)) continue;
    patch[field.name] = served;
  }
  return patch;
}

// ── Phase 532 — READ-ONLY DISPLAY ROWS (ARD-DSL-053 `rows`) ────────────────────────────────

/** What a display row shows where the served snapshot carries no value for its declared path. */
export const NOT_RECORDED = "Not recorded";

/**
 * Phase 559 — ONE LINE of a `shows: ledger` row: the "Risk ledger" panel of the approved
 * design (https://claude.ai/artifact/KqtoT3aU4QR1JryqxCfAvW) — label · source · signed points.
 *
 * Every part is DECLARED ({@link FormRowLedgerPresentation}) and read by the element key the
 * declaration names. Nothing is inferred from an element's own key, which is exactly what
 * stops `source_path` — an element holding a literal `${ctx…}` string — becoming a caption in
 * front of the person deciding a regulated file (L-97 rule c).
 */
export interface LedgerLine {
  /** The line's caption, already in plain English. Never an element key, never a raw token. */
  label: string;
  /** Where the line came from. Present iff the declaration declares the `source` column. */
  source?: string;
  /** The signed contribution. The RENDERER decides how a sign reads; this is the number. */
  points: number;
}

/**
 * Phase 559 — ONE LINE of a `shows: per_party` row over the binder's named projection
 * (`ctx._parties.per_party.<integration>.<field>` = `[{party, value}, …]`): who the line is
 * about, and what the file says about them — the value already read through the row's own
 * `words`, so a party's line can never read `check_failed`.
 */
export interface PartyLine {
  party: string;
  value: string;
}

/**
 * One display row, projected for rendering: the declaration's caption and the served value as
 * ONE line, a LIST of them, a LEDGER or a line PER PARTY.
 *
 * A list is its own shape rather than a joined string because a ledger row IS a list — the
 * per-factor contributions behind a score, the services on a file — and joining it would hand
 * the reader a sentence where the declaration promised items.
 *
 * <p>Phase 559 widens the value beyond text for the same reason: a ledger line is a caption, a
 * source and a SIGNED NUMBER, and a number whose sign carries meaning has to reach the renderer
 * as a number. Every arm is still chosen HERE, by the row's own `shows` — the renderer holds no
 * vocabulary of its own and reads no declaration.</p>
 *
 * <p>EMPTINESS IS UNIFORM, and it is the whole of the "nothing to show" contract: every arm
 * answers a value it cannot project with something of `length === 0` (the empty string, the
 * empty list). {@link displayRows} turns that into {@link NOT_RECORDED}; the evidence site
 * drops the row instead.</p>
 */
/**
 * Walk 2 — ONE ITEM of a MARKED LIST: the engine's generated code-list evidence row (a `from_ctx`
 * `table:` field's codes, each described). `marked` is the SERVER's verdict that this item matches
 * the question's flag — the renderer highlights it and prints the {@link MARK} beside it; the
 * row's `note` (the served legend) says what the mark means and cites its source.
 */
export interface MarkedLine {
  text: string;
  marked: boolean;
}

/** The marker a marked item carries — the same character the engine's legend opens with. */
export const MARK = "*";

export interface DisplayRow {
  label: string;
  value: string | string[] | LedgerLine[] | PartyLine[] | MarkedLine[];
  /** Walk 2 — one line under the value (a marked list's legend). PLAIN TEXT, served verbatim. */
  note?: string;
  /**
   * Walk 2 — what the value MEANS, from the row's declared `tones` map ({@link toneFor}):
   * the renderer colours it with the matching design-system feedback token. ABSENT ⇒ untoned.
   */
  tone?: FormRowTone;
}

/**
 * The DECLARED projection keys of one display row — everything {@link projectDisplayValue}
 * needs and nothing else. Deliberately not "a row": a card row carries its declaration to the
 * browser inside the form schema and holds a `path`, while an evidence row is resolved
 * server-side and holds a `value`, so the ONE projector takes the keys the two SHARE and
 * neither site can hand it a path to walk.
 */
export type DisplayRowProjection = Pick<
  FieldEvidenceRef,
  "shows" | "words" | "otherwise" | "presentation" | "per_party" | "tones"
>;

/**
 * Walk 2 — the TONE a scalar value carries under the row's declared `tones` map, looked up by
 * the value's STRING form (the `words` normalisation). Only a scalar is toned: a list, a ledger
 * or a per-party projection has no single meaning to colour. A tone outside the closed set —
 * possible only on a hand-edited canonical — is no tone at all, never a raw class name.
 */
export function toneFor(row: { tones?: FormRowTones } | undefined, value: unknown): FormRowTone | undefined {
  const tones = row?.tones;
  if (tones === undefined || tones === null || typeof tones !== "object") return undefined;
  if (value === undefined || value === null || typeof value === "object") return undefined;
  const key = String(value);
  if (!Object.prototype.hasOwnProperty.call(tones, key)) return undefined;
  const tone = (tones as Record<string, unknown>)[key];
  return typeof tone === "string" && (FORM_ROW_TONE_VALUES as readonly string[]).includes(tone)
    ? (tone as FormRowTone)
    : undefined;
}

/**
 * The rows a step card (or a `from_ctx.evidence` block) declares, resolved against the SERVED ctx
 * snapshot — the same `{label, path}` shape, the same path-only grammar and the same snapshot the
 * predicate evaluator reads, so there is one whitelist and the browser walks no ctx path of its own.
 *
 * Two rules, and they are the whole contract:
 *  - the resolved value is projected by the row's OWN declaration ({@link projectDisplayValue});
 *  - a path the snapshot does not carry — and a value the declaration gives no way to show —
 *    renders as an explicit {@link NOT_RECORDED}. A caption with nothing after it reads as a
 *    broken page; a caption that says "not recorded" is a FACT about the file, and on an
 *    assessment ledger it is one the reader has to be able to see.
 *
 * A row with no usable caption is dropped: there is nothing to label the value with.
 */
export function displayRows(
  rows: readonly FieldEvidenceRef[] | undefined,
  ctx: Readonly<Record<string, unknown>>,
): DisplayRow[] {
  const out: DisplayRow[] = [];
  for (const row of rows ?? []) {
    const declared = typeof row?.label === "string" ? row.label.trim() : "";
    if (declared.length === 0) continue;
    // Walk 2 — `label_from` reads the caption from the file (a company's own name); non-blank
    // text replaces the declared label, anything else keeps it. Same snapshot, same walk.
    const fromFile = typeof row.label_from === "string" ? readCtxRow(row.label_from, ctx) : undefined;
    const label = typeof fromFile === "string" && fromFile.trim().length > 0 ? fromFile.trim() : declared;

    // The path is resolved HERE, against the served snapshot, and the value is handed to the
    // ONE projector — the same call the evidence site makes with the value the SERVER read.
    const raw = readCtxRow(row.path, ctx);
    const projected = projectDisplayValue(row, raw);
    const tone = projected.length > 0 ? toneFor(row, raw) : undefined;
    out.push(tone === undefined
      ? { label, value: projected.length > 0 ? projected : NOT_RECORDED }
      : { label, value: projected, tone });
  }
  return out;
}

/**
 * Phase 559 (card #2116, L-97) — THE ONE PROJECTION of a served value into what a human reads.
 *
 * <p>Both display-row sites call exactly this: a step card's `rows` through
 * {@link displayRows}, and a question's `from_ctx.evidence` with the value the server resolved.
 * There is ONE closed key set behind a display row (`FORM_FROM_CTX_EVIDENCE_KEYS`,
 * packages/workflow-schema) and ONE deploy-time rule holding a row to what it declares
 * (ARD-DSL-062), so there is ONE projection — a second one would be the two-grammars-for-one-
 * thing defect in miniature.</p>
 *
 * <p><b>Every line comes from a DECLARATION.</b> Before this phase an unrecognised value fell
 * through to a generic `Key: Value` walk of the object, which is how the approval page of
 * 2026-09-21 put twenty-eight lines of `Input: pep, …, Source Path: ${ctx…}` in front of the
 * person deciding a regulated file. That walk is gone. A value the row has not said how to show
 * projects to NOTHING — {@link NOT_RECORDED} at the card site, a dropped row at the evidence
 * site — because "not recorded" is a fact a reader can act on and a raw ctx token is not
 * (L-97 rules c + d).</p>
 *
 * <p>The arms are the declared `shows` values and nothing else:</p>
 * <ul>
 *   <li>`words` — the value's STRING form through the row's own map; unmapped ⇒ `otherwise`;
 *       neither ⇒ nothing. NEVER the raw token.</li>
 *   <li>`number` / `text` — today's scalar projection ({@link toRowCell}), said out loud.</li>
 *   <li>`ledger` — an array of records as {@link LedgerLine}s, every column read by the element
 *       key the declaration names.</li>
 *   <li>`per_party` — the binder's named projection as {@link PartyLine}s, one per element.</li>
 *   <li>ABSENT — today's scalar projection, unchanged. This is what keeps every subject form
 *       and every not-yet-engaged staff form byte-identical: only the object dump is gone.</li>
 * </ul>
 */
export function projectDisplayValue(
  row: DisplayRowProjection | undefined,
  value: unknown,
): DisplayRow["value"] {
  switch (row?.shows) {
    case "words":
      return Array.isArray(value)
        ? value.map((item) => wordFor(row, item)).filter((item) => item.length > 0)
        : wordFor(row, value);
    case "ledger":
      return ledgerLines(row, value);
    case "per_party":
      return partyLines(row, value);
    // `number` and `text` are the EXPLICIT forms of the row that declares nothing, and an
    // undeclared row is still every subject form on disk — one arm, deliberately.
    case "number":
    case "text":
    default:
      return scalarValue(value);
  }
}

/**
 * Today's scalar projection, minus the object walk: an ARRAY is one line per element (a list
 * stays a list), anything else is one line, and a value with no scalar reading — an object, a
 * null — is nothing at all.
 */
function scalarValue(value: unknown): string | string[] {
  if (Array.isArray(value)) {
    return value.map((item) => toRowCell(item)).filter((item) => item.length > 0);
  }
  return toRowCell(value);
}

/**
 * ONE value through a declared `words` map.
 *
 * The lookup key is the value's STRING form — a boolean `true` finds the QUOTED key `"true"`,
 * the same normalisation the deploy-time rule applies to a `rank:` of booleans, so what lint
 * held the author to is what the browser looks up. A value the map does not carry reads as the
 * declared `otherwise`, and a row that declares neither shows NOTHING: the raw token is never
 * an answer. A value ABSENT from the file is also nothing — which is what leaves the two sites
 * free to differ on it (NOT_RECORDED here, a dropped row there).
 */
function wordFor(row: { words?: FormRowWords; otherwise?: string }, value: unknown): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") return "";
  const mapped = lookupWord(row.words, String(value));
  if (mapped !== undefined) return mapped;
  return typeof row.otherwise === "string" ? row.otherwise.trim() : "";
}

/** An OWN-property lookup in a declared map — a declaration never reaches a prototype key. */
function lookupWord(words: FormRowWords | undefined, key: string): string | undefined {
  if (words === undefined || words === null || typeof words !== "object") return undefined;
  if (!Object.prototype.hasOwnProperty.call(words, key)) return undefined;
  const mapped = (words as Record<string, unknown>)[key];
  if (typeof mapped !== "string" || mapped.trim().length === 0) return undefined;
  return mapped;
}

/**
 * `shows: ledger` — an array of records as the approved design's "Risk ledger" lines.
 *
 * Every part of a line is DECLARED: the caption, the optional source and the number each name
 * the ELEMENT KEY they are read from. Nothing is inferred from an element's own keys, so a
 * record carrying `source_path: "${ctx…}"` contributes that key to nothing.
 *
 * A record whose declared `points` key holds no number is not a ledger line and is dropped: a
 * ledger is an arithmetic, and a line with no arithmetic in it is noise. `hide_zero` ABSENT
 * means TRUE — the twenty-eight-line page of 2026-09-21 was mostly factors that scored nothing
 * — and `hide_zero: false` keeps them for the reader who needs the whole working.
 */
function ledgerLines(row: DisplayRowProjection, value: unknown): LedgerLine[] | string {
  const presentation = row.presentation;
  if (!Array.isArray(value) || presentation === undefined || presentation === null) return "";
  if (presentation.kind !== "ledger_line") return "";
  const columns = presentation.columns;
  if (columns === undefined || columns === null) return "";
  const hideZero = presentation.hide_zero !== false;

  const lines: LedgerLine[] = [];
  for (const element of value) {
    if (!isRecord(element)) continue;
    const points = asPoints(columns.points, element);
    if (points === undefined) continue;
    if (hideZero && points === 0) continue;
    const line: LedgerLine = { label: columnText(columns.label, element, row), points };
    // A source the row did NOT declare is absent; one it declared and the file cannot answer
    // says so, for the same reason a captioned row does.
    if (columns.source !== undefined) line.source = columnText(columns.source, element, row);
    lines.push(line);
  }
  return lines;
}

/** One TEXT column of a ledger line: the declared element key, through the column's own words. */
function columnText(
  column: FormRowLedgerColumn | undefined,
  element: Record<string, unknown>,
  row: DisplayRowProjection,
): string {
  const raw = elementValue(column?.path, element);
  // A column WITH a map is held to it. A column with NO map shows its value as text, because
  // the source of a line is already a name the author wrote ("ACCA assessment") — but only a
  // STRING or a NUMBER is a name. A bare boolean has no English of its own, and `true` is one
  // of the exact tokens that reached a human on 2026-09-21 (L-97 rule d), so an unmapped
  // boolean is nothing rather than the word "true".
  const projected = column?.words === undefined ? plainText(raw) : wordFor({ words: column.words }, raw);
  if (projected.length > 0) return projected;
  const otherwise = typeof row.otherwise === "string" ? row.otherwise.trim() : "";
  return otherwise.length > 0 ? otherwise : NOT_RECORDED;
}

/** A value that is already its own English: a string or a number. Anything else is nothing. */
function plainText(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") return toRowCell(value);
  return "";
}

/** The `points` column: the declared element key read as a NUMBER, or nothing. */
function asPoints(column: { path: string } | undefined, element: Record<string, unknown>): number | undefined {
  const raw = elementValue(column?.path, element);
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : undefined;
  // The engine writes JSON numbers; a string is accepted because a connector envelope copied
  // through ctx may carry one, and a number quoted is still the same arithmetic.
  if (typeof raw === "string" && raw.trim().length > 0) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** ONE key of ONE element, by own-property lookup. A key is never a path and never walked. */
function elementValue(key: string | undefined, element: Record<string, unknown>): unknown {
  if (typeof key !== "string") return undefined;
  const trimmed = key.trim();
  if (trimmed.length === 0) return undefined;
  if (!Object.prototype.hasOwnProperty.call(element, trimmed)) return undefined;
  return element[trimmed];
}

/**
 * `shows: per_party` — the binder's named projection (`[{party, value}, …]`) as one line per
 * party. The NAME is a name and passes through as text; the VALUE goes through the row's own
 * `words`, so the per-party screen outcome reads as English exactly as the single-party one
 * does. Which element keys carry the two is DECLARED, defaulting to the pair the binder emits.
 */
function partyLines(row: DisplayRowProjection, value: unknown): PartyLine[] | string {
  if (!Array.isArray(value)) return "";
  const nameKey = trimmedOr(row.per_party?.name, PER_PARTY_NAME_KEY);
  const valueKey = trimmedOr(row.per_party?.value, PER_PARTY_VALUE_KEY);

  const lines: PartyLine[] = [];
  for (const element of value) {
    if (!isRecord(element)) continue;
    const party = plainText(elementValue(nameKey, element));
    const projected = wordFor(row, elementValue(valueKey, element));
    lines.push({
      party: party.length > 0 ? party : NOT_RECORDED,
      value: projected.length > 0 ? projected : NOT_RECORDED,
    });
  }
  return lines;
}

/** The element keys `ctx._parties.per_party` emits (PartyLedgerCtxBinder) — the declared default. */
const PER_PARTY_NAME_KEY = "party";
const PER_PARTY_VALUE_KEY = "value";

function trimmedOr(declared: string | undefined, fallback: string): string {
  return typeof declared === "string" && declared.trim().length > 0 ? declared.trim() : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// ── Phase 449 — the answer summary ─────────────────────────────────────────────────────────

/** What a review step shows where an answer is missing. */
export const NOT_ANSWERED = "Not answered";

/** How a `boolean` answer reads back to the subject. */
export const ANSWER_YES = "Yes";
export const ANSWER_NO = "No";

/** One answered field on the review step. `name` is the declared key (a stable React key). */
export interface SummaryRow {
  name: string;
  label: string;
  display: string;
  /**
   * A DISPLAY format preset's read-back of the same answer, one line per entry (formats.ts
   * `formatHints`). Present only when the field declares such a preset and has an answer; the
   * review step reads these back instead of `display`, so the answer the subject confirms
   * reads exactly as it did under the input they typed it into.
   *
   * `display` is still the raw answer, and the SUBMITTED value is built by `buildPayload` from
   * `values` — never from a summary row — so a description cannot reach the payload.
   */
  lines?: string[];
}

/** One step's block of the review summary. */
export interface SummaryStep {
  /** Position in `visibleSteps(schema, scope)` — what a "Change" control jumps back to. */
  index: number;
  title: string;
  rows: SummaryRow[];
}

/**
 * Every visible answer of every visible step, projected for reading rather than editing.
 *
 * The projection lives HERE and not in the review component for the same reason the rest of
 * this module does: the rule "a choice displays its LABEL, never its wire value" is a property
 * of the grammar, not of one component, and the component that renders it holds no schema.
 *
 * A REVIEW step is excluded from its own summary (it has no answers of its own), as is a step
 * with nothing to show — a content-only introduction is not a heading with an empty list under
 * it. `index` is the position in the VISIBLE step list, so a caller can hand it straight back
 * to whatever selects the current step.
 */
export function answerSummary(
  schema: FormSchema,
  values: FormValues,
  scope: PredicateScope,
): SummaryStep[] {
  const summary: SummaryStep[] = [];

  visibleSteps(schema, scope).forEach((step, index) => {
    if (step.review) return;
    const rows = visibleFields(step.fields, scope).flatMap((field) =>
      summaryRows(field, values, scope),
    );
    if (rows.length > 0) summary.push({ index, title: stepTitle(step), rows });
  });

  return summary;
}

function stepTitle(step: FormStep): string {
  return step.title ?? humanise(step.id);
}

/** One field's contribution: one row for a scalar, one row PER ROW for a repeating group. */
function summaryRows(field: FieldSpec, values: FormValues, scope: PredicateScope): SummaryRow[] {
  if (isGroupField(field)) return groupSummaryRows(field, values, scope);
  // Card #2878 — a hidden field is never in front of the person, so not on the review either.
  if (field.hidden === true) return [];
  // A valueless read_only field is not on the page, so it is not on the review either; a filled
  // one reads back exactly as any other answer does (label, not token; described lines).
  if (field.read_only === true && readOnlyShown(field, values) === null) return [];
  const row: SummaryRow = {
    name: field.name,
    label: fieldLabel(field),
    display: scalarDisplay(field, values),
  };
  const lines = formatHints(field.format, values[field.name]);
  return [lines ? { ...row, lines } : row];
}

function groupSummaryRows(
  group: GroupFieldSpec,
  values: FormValues,
  scope: PredicateScope,
): SummaryRow[] {
  const rows = asRows(values[group.name]);
  const label = fieldLabel(group);
  if (rows.length === 0) {
    return [{ name: group.name, label, display: NOT_ANSWERED }];
  }

  // Row-scoped key, same `<group>.<rowIndex>` shape the error map uses — a stable React key.
  return rows.map((row, index) => ({
    name: `${group.name}.${index}`,
    label: `${label} ${index + 1}`,
    display: rowDisplay(group, row, scope),
  }));
}

/** One group row as one line: each answered subfield as `<label>: <display>`. */
function rowDisplay(group: GroupFieldSpec, row: GroupRow, scope: PredicateScope): string {
  // Walk 3 (ARD-DSL-070) — a locked row reads back as its eyebrow and its declared facts.
  if (isLockedRow(group, row, scope)) {
    const facts = lockedRowFacts(group, row).map((f) => `${f.label}: ${f.display}`);
    return [group.locked_rows!.eyebrow, ...facts].join(GROUP_LIST_SEPARATOR);
  }
  const cells: string[] = [];
  for (const sub of visibleFields(group.fields, rowScope(scope, row))) {
    if ((sub.type as string) === "group") continue;
    if (sub.read_only === true && readOnlyShown(sub, row) === null) continue;
    // A group row is one LINE, so a display preset's read-back is joined into it rather than
    // stacked — the same described entries, in the shape this row can carry.
    const described = formatHints(sub.format, row[sub.name]);
    const text = described ? described.join(GROUP_LIST_SEPARATOR) : scalarDisplay(sub, row);
    if (text === NOT_ANSWERED) continue;
    cells.push(`${fieldLabel(sub)}: ${text}`);
  }
  return cells.length > 0 ? cells.join(GROUP_LIST_SEPARATOR) : NOT_ANSWERED;
}

/**
 * One scalar answer as the subject should read it back. An ABSENT key is unanswered — this
 * module's invariant — which is why a `boolean` the subject has never touched reads
 * "Not answered" while one they ticked and unticked reads "No".
 */
export function scalarDisplay(field: ScalarFieldSpec, values: FormValues): string {
  if (field.type === "address") {
    const a = asAddress(values[field.name]);
    return isAddressEmpty(a) ? NOT_ANSWERED : formatAddress(a);
  }
  const answered = Object.prototype.hasOwnProperty.call(values, field.name);

  if (field.type === "boolean") {
    if (!answered) return NOT_ANSWERED;
    return asBoolean(values[field.name]) ? ANSWER_YES : ANSWER_NO;
  }

  if (field.type === "checkbox_group") {
    // A GROUP-ROW cell holds the selections JOINED; a top-level multi-select holds them as a
    // real `string[]`. `fromRowCell` reads both, so each selection resolves to its OWN label
    // instead of the whole joined cell being looked up as one option value and missing.
    const selections = asStringArray(fromRowCell(field.type, values[field.name]));
    const labels = selections.map((value) => optionLabel(field, value));
    return labels.length > 0 ? labels.join(GROUP_LIST_SEPARATOR) : NOT_ANSWERED;
  }

  const text = asString(values[field.name]).trim();
  if (text.length === 0) return NOT_ANSWERED;
  // A choice answer reads back as its LABEL; every other scalar reads back as what was typed.
  if (field.sensitive) return maskSensitive(text);
  if (field.type === "date") return formatDate(text);
  return CHOICE_FIELD_TYPES.includes(field.type) ? optionLabel(field, text) : text;
}

// ── Phase 572 wave 6 task 6.2 — a declared read_only field, read back ───────────────────────

/**
 * What a declared `read_only` field shows in place of a control — already formatted for
 * reading, in exactly one of three shapes the ONE no-control presentation (`ReadOnlyValue`)
 * takes as props, so a caller spreads it straight on:
 *
 *  - `lines` — a DISPLAY format preset's read-back ({@link formatHints}), one entry per line
 *    (`sic_codes`: "62012 — Business and domestic software development");
 *  - `address` — a structured address, shown as the existing read-only address block;
 *  - `value` — everything else as plain text through {@link scalarDisplay}, so a choice reads as
 *    its option LABEL, a boolean as Yes/No, a date as a date — never a wire token or `true`.
 */
export type ReadOnlyShown = { lines: string[] } | { address: AddressValue } | { value: string };

/** An unresolved `${…}` template: a source that did not resolve, never a value to print. */
const UNRESOLVED_TEMPLATE = /\$\{[^}]*\}/;

function holdsTemplate(value: FieldValue | undefined): boolean {
  if (typeof value === "string") return UNRESOLVED_TEMPLATE.test(value);
  if (Array.isArray(value)) return value.some((item) => typeof item === "string" && UNRESOLVED_TEMPLATE.test(item));
  return false;
}

/**
 * The read-back of one `read_only` field, or `null` when it has NO VALUE — an empty or
 * whitespace string, an empty selection, an all-blank address, an untouched boolean, or a value
 * that is still a `${ctx…}` path (L-97 rule d: a path is never shown). `null` is the ONE rule
 * behind "renders nothing and is absent from the review", shared by the renderer (top level and
 * group subfield) and the summary above.
 *
 * `values` is the answer set the field is keyed in — the form's answers for a top-level field,
 * the ROW for a group subfield (a row cell is read through the same {@link scalarDisplay} rule
 * the review's row line uses). A VIEW only: nothing here writes an answer, so the submitted
 * payload is exactly what it would be without `read_only`.
 */
export function readOnlyShown(field: ScalarFieldSpec, values: FormValues): ReadOnlyShown | null {
  const raw = values[field.name];
  if (holdsTemplate(raw)) return null;
  if (field.type === "address") {
    const address = asAddress(raw);
    return isAddressEmpty(address) ? null : { address };
  }
  if (field.type === "boolean") {
    if (!Object.prototype.hasOwnProperty.call(values, field.name)) return null;
  } else if (field.type === "checkbox_group") {
    if (asStringArray(fromRowCell(field.type, raw)).length === 0) return null;
  } else if (asString(raw).trim().length === 0) {
    return null;
  }
  const text = scalarDisplay(field, values);
  const lines = formatHints(field.format, raw);
  return lines ? { lines } : { value: text };
}

// ── Self-match: which register row is the person filling the form (Phase 582 / 563 5.1) ─────

/**
 * The honorific tokens a person-name comparison ignores, so `"Mr Carlos Oliveira"` and
 * `"Carlos Oliveira"` read as the same person. The TS mirror of the platform's ONE honorific
 * vocabulary — `PersonNameTokens.HonorificTokens` (platform/core/Text/PersonNameTokens.cs); add or
 * remove a token in both places, never in one.
 */
export const HONORIFIC_TOKENS: ReadonlySet<string> = new Set([
  "mr", "mrs", "ms", "miss", "mx", "dr", "prof", "professor",
  "sir", "dame", "lord", "lady", "rev", "hon", "honourable", "the",
]);

/**
 * The corporate-suffix tokens that mark a name as a COMPANY, whose `ms` / `dr` is part of the name
 * rather than a title — the mirror of `PersonNameTokens.CorporateSuffixTokens`, applied BEFORE the
 * honorific drop exactly as the platform applies it (`IsCorporateName`, then `DropHonorifics`).
 */
export const CORPORATE_SUFFIX_TOKENS: ReadonlySet<string> = new Set([
  "limited", "ltd", "plc", "llp", "llc", "inc", "corp", "group",
]);

/**
 * A person name as its SET of comparable tokens: lowercased, split on anything that is not a
 * letter or digit (so punctuation — the register's `"OLIVEIRA, Carlos"` comma, `"Dr."`'s dot —
 * never survives), honorifics dropped. Order-free on purpose: the officer register writes
 * `SURNAME, Forename`, the PSC register and the filler write `Forename Surname`.
 *
 * <p>Same fail-safe as the platform's `DropHonorifics`: a name that is ONLY a title keeps its
 * tokens rather than reducing to nothing; and a corporate name keeps them all.</p>
 */
export function personNameTokens(raw: string): Set<string> {
  const tokens = raw.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((t) => t.length > 0);
  if (tokens.some((t) => CORPORATE_SUFFIX_TOKENS.has(t))) return new Set(tokens);
  const kept = tokens.filter((t) => !HONORIFIC_TOKENS.has(t));
  return new Set(kept.length > 0 ? kept : tokens);
}

/**
 * A person name as its FORENAME and SURNAME — the two tokens a self-match compares (BB walk 2).
 *
 * <p>Lowercased and split exactly as {@link personNameTokens} splits, honorifics dropped. The
 * register's comma order is honoured: `"OLIVEIRA, Carlos Fernando"` (the officers register writes
 * `SURNAME, Forenames`) reads forename `carlos`, surname `oliveira`, the same as the PSC register's
 * and the filler's `"Carlos Fernando Oliveira"`. Everything between the first forename and the
 * surname is a MIDDLE name and is ignored on both sides.</p>
 *
 * <p>`null` when the name cannot say who someone is: fewer than two name tokens (a lone forename,
 * a bare title, an empty answer), or a CORPORATE name (`… Limited`), which is never a person.</p>
 */
export function personNameParts(raw: string): { first: string; last: string; middles: string[] } | null {
  const tokenise = (text: string) => text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((t) => t.length > 0);
  const all = tokenise(raw);
  if (all.some((t) => CORPORATE_SUFFIX_TOKENS.has(t))) return null;
  const named = (tokens: string[]) => tokens.filter((t) => !HONORIFIC_TOKENS.has(t));
  const comma = raw.indexOf(",");
  if (comma >= 0) {
    const surname = named(tokenise(raw.slice(0, comma)));
    const forenames = named(tokenise(raw.slice(comma + 1)));
    if (surname.length > 0 && forenames.length > 0)
      return { first: forenames[0]!, last: surname[surname.length - 1]!, middles: forenames.slice(1) };
  }
  const tokens = named(all);
  if (tokens.length < 2) return null;
  return { first: tokens[0]!, last: tokens[tokens.length - 1]!, middles: tokens.slice(1, -1) };
}

/**
 * Is `row` the same person as `filler`? (BB walk 2, 24 Sep.) Case-insensitive, titles ignored,
 * middle names ignored unless BOTH sides carry some (then one set must contain the other),
 * register comma order honoured — and the FORENAME and the SURNAME must BOTH match
 * ({@link personNameParts}). So `"Carlos Oliveira"`, `"Mr Carlos Fernando Oliveira"` and
 * `"OLIVEIRA, Carlos"` are one person; `"Carlos Santos"` and `"Fernando Oliveira"` are not.
 *
 * <p>The filler must bring at least a forename AND a surname: a lone `"Carlos"` is not enough to
 * say which person on a register is them, and an empty answer matches nothing.</p>
 */
export function personNameMatches(filler: string, row: string): boolean {
  const wanted = personNameParts(filler);
  const have = personNameParts(row);
  if (wanted === null || have === null) return false;
  if (wanted.first !== have.first || wanted.last !== have.last) return false;
  // Walk 2 cold review fix 2 — a middle name is ignored only when ONE side has none. When BOTH
  // carry middle names, one set must be contained in the other: "John Michael Smith" is not the
  // register's "SMITH, John Paul" (a father and son share first + last names).
  if (wanted.middles.length === 0 || have.middles.length === 0) return true;
  const contains = (a: string[], b: string[]) => b.every((m) => a.includes(m));
  return contains(wanted.middles, have.middles) || contains(have.middles, wanted.middles);
}

/**
 * The ONE row of a `self_match` group whose `row_field` names the person filling the form, or
 * `null` — when the group declares no `self_match` (opt-in by presence: nothing is matched at
 * all), when no row matches, or when TWO OR MORE do (an ambiguous register is never guessed at).
 */
export function selfMatchRow(group: GroupFieldSpec, rows: readonly GroupRow[], values: FormValues): number | null {
  const match = group.self_match;
  if (!match) return null;
  const filler = asString(values[match.filler_field]);
  let found: number | null = null;
  for (let i = 0; i < rows.length; i += 1) {
    // Walk 3 (ARD-DSL-070) — a locked row (e.g. a company) is never "this is me".
    if (isLockedRow(group, rows[i]!, { ctx: {}, fields: values, party: {} })) continue;
    if (!personNameMatches(filler, asString(fromRowCell("string", rows[i]![match.row_field])))) continue;
    if (found !== null) return null;
    found = i;
  }
  return found;
}

/**
 * Card #3086 — the cells `self_match.seed_from` writes onto the filler's OWN row, so they never
 * type twice what they already gave at the top of the form (a mobile from the phone number).
 *
 * The caller writes them in the SAME write as the self-match tick, to the ONE row that tick lands
 * on: the row {@link selfMatchRow} names, ticked on first render, or (card #3090) a row the
 * subject ticks "this is me" by hand. This function judges cells, never rows or ticks. For each
 * `<subfield>: <top-level field>` entry the cell is written only when it is still BLANK
 * ({@link isBlankCell} — a resumed draft or a register pre-fill wins) and the top-level answer is
 * not blank (nothing to carry ⇒ nothing written). The value is that answer VERBATIM: an ordinary
 * answer the subject can change, never re-synced afterwards. `{}` when the group declares no
 * `seed_from` (opt-in by presence).
 */
export function selfMatchSeed(group: GroupFieldSpec, row: GroupRow, values: FormValues): GroupRow {
  const cells: GroupRow = {};
  const seeds = group.self_match?.seed_from;
  if (!seeds) return cells;
  const declared = new Set((group.fields ?? []).map((sub) => sub.name));
  for (const [subfield, source] of Object.entries(seeds)) {
    if (!declared.has(subfield) || !isBlankCell(row[subfield])) continue;
    const answer = asString(values[source]);
    if (answer.trim().length === 0) continue;
    cells[subfield] = answer;
  }
  return cells;
}

/**
 * Card #3086 — the cells to CLEAR when the subject unticks `tick_field` on a row: each
 * `seed_from` cell on that row that still equals, exactly, the top-level answer it seeds from.
 * That row is now someone else's, and the filler's own number left on it would pass the
 * required check and go into that person's identity check. A cell the subject changed differs
 * from the answer and is kept. STATELESS (it compares with the live answers, remembering no
 * seed), so a removed or reordered row can never clear the wrong cell. By the same token it also
 * clears a cell that was NEVER seeded (a row ticked by hand, its mobile typed equal to the
 * top-level phone): that is intended, not a side effect — the filler's own answer does not belong
 * on a row that is not theirs, however it got there. `{}` when the group declares no `seed_from`.
 */
export function selfMatchUnseed(group: GroupFieldSpec, row: GroupRow, values: FormValues): GroupRow {
  const cells: GroupRow = {};
  const seeds = group.self_match?.seed_from;
  if (!seeds) return cells;
  for (const [subfield, source] of Object.entries(seeds)) {
    const answer = asString(values[source]);
    if (answer.trim().length === 0 || row[subfield] !== answer) continue;
    cells[subfield] = "";
  }
  return cells;
}

// ── Likely duplicates: two rows naming the same person (card #2506) ──────────────────────────────

/**
 * Peerage ranks — STYLING only directly after "the" / "hon" / "honourable" ("The Viscount William
 * …"); anywhere else a rank word is a name ("Earl Jones"). The mirror of the platform's
 * `PersonNamePresentation.PeerageRanks` (platform/core/Text/PersonNamePresentation.cs).
 */
const PEERAGE_RANKS: ReadonlySet<string> = new Set([
  "duke", "duchess", "marquess", "marquis", "marchioness", "earl", "countess",
  "viscount", "viscountess", "baron", "baroness",
]);
const STYLE_WORDS: ReadonlySet<string> = new Set(["the", "hon", "honourable"]);
const EXTRA_TITLE_WORDS: ReadonlySet<string> = new Set(["rt", "right", "reverend"]);

/** Lower-cased letter/digit runs of one word: "Pleydell-Bouverie" → ["pleydell", "bouverie"]. */
function nameParts(word: string): string[] {
  return word.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((t) => t.length > 0);
}

/** Whitespace words, lower-cased, trimmed of '.' and ','; empties dropped. */
function nameWords(text: string): string[] {
  return text.toLowerCase().split(/\s+/).map((w) => w.replace(/^[.,]+|[.,]+$/g, "")).filter((w) => w.length > 0);
}

/** Drops a peerage rank after a style word, then every honorific (never down to nothing). */
function unstyled(words: string[]): string[] {
  const ranked = words.filter((w, i) => !(i > 0 && PEERAGE_RANKS.has(w) && STYLE_WORDS.has(words[i - 1]!)));
  const kept = ranked.filter((w) => !HONORIFIC_TOKENS.has(w));
  return kept.length > 0 ? kept : ranked;
}

function isTitleOnly(text: string): boolean {
  const words = nameWords(text);
  return words.length > 0 && words.every((w) => HONORIFIC_TOKENS.has(w) || PEERAGE_RANKS.has(w) || EXTRA_TITLE_WORDS.has(w));
}

/**
 * Review note 1 — the generation a bracketed aside names, when ALL its words are generational
 * (jnr, jr, snr, sr, junior, senior, ii, iii, iv; any case, with or without a trailing dot):
 * "(Jnr.)" → "junior", "(SR)" → "senior", "(III)" → "iii". Card #2933: an ordinal peerage aside
 * is a generation too (`ordinalPeerageOf`): "(The 2nd Baron Y)" → "2 baron y". `null` for any
 * other aside ("(Junior Partner)", "(Known As Viscount Folkestone)"). The same asides the server
 * keeps in its party key (`PersonNamePresentation.IsGenerationalAside` / `OrdinalPeerageOf`).
 */
const GENERATION_WORDS = new Map<string, string>([
  ["jnr", "junior"], ["jr", "junior"], ["junior", "junior"],
  ["snr", "senior"], ["sr", "senior"], ["senior", "senior"],
  ["ii", "ii"], ["iii", "iii"], ["iv", "iv"],
]);

function generationOf(aside: string): string | null {
  const words = aside.trim().replace(/^\(|\)$/g, "").trim().toLowerCase().split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return null;
  const named = words.map((w) => GENERATION_WORDS.get(w.replace(/\.$/, "")));
  if (named.every((g) => g !== undefined)) return named.join(" ");
  return ordinalPeerageOf(aside);
}

/**
 * Card #2933 — an ORDINAL PEERAGE aside is a generation too: "John X (The 2nd Baron Y)" and
 * "John X (The 3rd Baron Y)" are father and son. Its reading is the ordinal as a number, then the
 * rank and the rest of the title, lower-cased: "(The 2nd Baron Y)" and "(The Second Baron Y)" →
 * "2 baron y"; "(THE 9TH EARL OF RADNOR)" → "9 earl of radnor". The aside's words (trailing dots
 * and commas dropped) must be an optional "the", an ordinal (digits "2nd" / "21st", or a word
 * "Second" / "Eighth" / "Twenty-First" / "Twenty First"), then a peerage rank (or "baronet").
 * `null` for any other aside ("(The Earl Of Radnor)", "(Known As Viscount Folkestone)"). The
 * mirror of the platform's `PersonNamePresentation.OrdinalPeerageOf`, so the form's duplicate
 * check and the server's party key agree.
 */
const ORDINAL_WORDS = new Map<string, number>([
  ["first", 1], ["second", 2], ["third", 3], ["fourth", 4], ["fifth", 5],
  ["sixth", 6], ["seventh", 7], ["eighth", 8], ["ninth", 9], ["tenth", 10],
  ["eleventh", 11], ["twelfth", 12], ["thirteenth", 13], ["fourteenth", 14],
  ["fifteenth", 15], ["sixteenth", 16], ["seventeenth", 17], ["eighteenth", 18],
  ["nineteenth", 19], ["twentieth", 20], ["thirtieth", 30], ["fortieth", 40],
  ["fiftieth", 50], ["sixtieth", 60], ["seventieth", 70], ["eightieth", 80],
  ["ninetieth", 90],
]);
const TENS_WORDS = new Map<string, number>([
  ["twenty", 20], ["thirty", 30], ["forty", 40], ["fifty", 50],
  ["sixty", 60], ["seventy", 70], ["eighty", 80], ["ninety", 90],
]);
const ORDINAL_PEERAGE_RANKS: ReadonlySet<string> = new Set([...PEERAGE_RANKS, "baronet"]);

function ordinalValue(word: string): number | null {
  const digits = /^([0-9]{1,3})(?:st|nd|rd|th)$/.exec(word);
  if (digits) return Number(digits[1]);
  const named = ORDINAL_WORDS.get(word);
  if (named !== undefined) return named;
  const dash = word.indexOf("-");
  if (dash > 0) {
    const tens = TENS_WORDS.get(word.slice(0, dash));
    const unit = ORDINAL_WORDS.get(word.slice(dash + 1));
    if (tens !== undefined && unit !== undefined && unit < 10) return tens + unit;
  }
  return null;
}

export function ordinalPeerageOf(aside: string): string | null {
  const t = aside.trim();
  if (t.length < 2 || !t.startsWith("(") || !t.endsWith(")")) return null;
  const words = t.slice(1, -1).trim().toLowerCase().split(/\s+/).map((w) => w.replace(/[.,]+$/, "")).filter((w) => w.length > 0);
  let i = 0;
  if (words[i] === "the") i += 1;
  if (i >= words.length) return null;
  let n = ordinalValue(words[i]!);
  let used = 1;
  if (n === null && i + 1 < words.length) {
    const tens = TENS_WORDS.get(words[i]!);
    const unit = ORDINAL_WORDS.get(words[i + 1]!);
    if (tens !== undefined && unit !== undefined && unit < 10) {
      n = tens + unit;
      used = 2;
    }
  }
  if (n === null || n <= 0) return null;
  i += used;
  if (i >= words.length || !ORDINAL_PEERAGE_RANKS.has(words[i]!)) return null;
  return [String(n), ...words.slice(i)].join(" ");
}

/**
 * A person name as the parts a likely-duplicate check compares: the FIRST FORENAME (`first`), the
 * SURNAME's parts (`surname`), every part after the first forename (`tail`, which ends with
 * the surname), and the GENERATION a bracketed aside names (`generation`: "junior", "senior",
 * "iii", or "" when there is none). `null` for a company, a blank, or a single word.
 *
 * <p>A generational aside ("(Junior)", "(Jr.)", "(III)") is part of the name — it tells father
 * from son — but it is no name part: it is read into `generation`, never into `tail`.</p>
 *
 * <p>Titles are not the name: any other bracketed aside ("(Known As Viscount Folkestone)"), the
 * register's trailing title section ("…, Peter John, The Honourable"), "The" / "Hon" /
 * "Honourable" / "Mr" … and a rank after "The" are all dropped. Only a company register's officer
 * form ("SURNAME, Forenames" with an all-capitals surname) is read surname-first; any other comma
 * is a separator. Hyphens split a surname into parts, so "Pleydell-Bouverie" and the register's
 * "PLEYDELL BOUVERIE" are the same two parts.</p>
 */
export function duplicateNameKey(raw: string): { first: string; surname: string[]; tail: string[]; generation: string } | null {
  if (raw.trim().length === 0) return null;
  if (nameParts(raw).some((t) => CORPORATE_SUFFIX_TOKENS.has(t))) return null;
  const generations: string[] = [];
  const text = raw.replace(/\s*\([^()]*\)/g, (aside) => {
    const g = generationOf(aside);
    if (g !== null) generations.push(g);
    return " ";
  });
  const generation = generations.join(" ");
  const sections = text.split(",");
  const head = sections.length >= 3 && sections.slice(2).every(isTitleOnly) ? sections.slice(0, 2) : sections;
  const before = head[0] ?? "";
  if (head.length >= 2 && /\p{L}/u.test(before) && !/\p{Ll}/u.test(before) && head.slice(1).join(" ").trim().length > 0) {
    const surname = unstyled(nameWords(before)).flatMap(nameParts);
    const forenames = unstyled(nameWords(head.slice(1).join(" "))).flatMap(nameParts);
    if (surname.length === 0 || forenames.length === 0) return null;
    return { first: forenames[0]!, surname, tail: [...forenames.slice(1), ...surname], generation };
  }
  const words = unstyled(nameWords(head.join(" ")));
  if (words.length < 2) return null;
  const first = nameParts(words[0]!).join("");
  const surname = nameParts(words[words.length - 1]!);
  const tail = words.slice(1).flatMap(nameParts);
  if (first.length === 0 || surname.length === 0) return null;
  return { first, surname, tail, generation };
}

/**
 * Do two names look like the same person? (Card #2506.) The same FIRST FORENAME and the same
 * SURNAME, ignoring case, hyphens and titles — so "Peter John Pleydell Bouverie" (the officer
 * register, no hyphen) and "The Honourable Peter John Pleydell-Bouverie" (the PSC register) do,
 * and "Anna Smith-Jones" / "Anna Jones" do not. Middle names are not compared: this only ever
 * raises a QUESTION for the person filling the form, who decides. Two DIFFERENT generations
 * ("John Smith (Junior)" / "John Smith (Senior)", or card #2933's "John X (The 2nd Baron Y)" /
 * "John X (The 3rd Baron Y)") are father and son, never a likely duplicate.
 */
export function likelyDuplicateNames(a: string, b: string): boolean {
  const ka = duplicateNameKey(a);
  const kb = duplicateNameKey(b);
  if (ka === null || kb === null || ka.first !== kb.first) return false;
  if (ka.generation !== "" && kb.generation !== "" && ka.generation !== kb.generation) return false;
  const endsWith = (pool: string[], parts: string[]) =>
    parts.length <= pool.length && parts.every((p, i) => pool[pool.length - parts.length + i] === p);
  return endsWith(kb.tail, ka.surname) && endsWith(ka.tail, kb.surname);
}

/**
 * Review note 2 — two likely-duplicate names that still DIFFER beyond the first forename, so the
 * prompt must ask ("Is this the same person as …?") rather than assert ("This looks like …"):
 * both carry middle names and they are not the same ("Peter John X" / "Peter James X"), or only
 * one names a generation ("John Smith (Junior)" / "John Smith"). One with no middle names, or the
 * same middle names spelled with or without a hyphen, is no conflict. `false` for names that are
 * not likely duplicates at all.
 */
export function duplicateNeedsAsking(a: string, b: string): boolean {
  if (!likelyDuplicateNames(a, b)) return false;
  const ka = duplicateNameKey(a)!;
  const kb = duplicateNameKey(b)!;
  if ((ka.generation === "") !== (kb.generation === "")) return true;
  // Both tails end with the longer of the two surnames (likelyDuplicateNames checked it), so
  // what is left before it is each name's middle names.
  const surnameLength = Math.max(ka.surname.length, kb.surname.length);
  const ma = ka.tail.slice(0, ka.tail.length - surnameLength);
  const mb = kb.tail.slice(0, kb.tail.length - surnameLength);
  return ma.length > 0 && mb.length > 0 && ma.join(" ") !== mb.join(" ");
}

/**
 * The rows of a `likely_duplicates` group that look like another row's person: row index → the
 * indexes of the rows it looks like, in row order. Empty when the group does not declare
 * `likely_duplicates` (opt-in by presence). A locked row (a company) and a row with no name take
 * no part.
 */
export function likelyDuplicateRows(group: GroupFieldSpec, rows: readonly GroupRow[], scope: PredicateScope): Map<number, number[]> {
  const found = new Map<number, number[]>();
  const decl = group.likely_duplicates;
  if (!decl) return found;
  const names = rows.map((row) =>
    isLockedRow(group, row, scope) ? "" : asString(fromRowCell("string", row[decl.name_field])).trim(),
  );
  for (let i = 0; i < rows.length; i += 1) {
    if (names[i]!.length === 0) continue;
    for (let j = 0; j < rows.length; j += 1) {
      if (i === j || names[j]!.length === 0) continue;
      if (!likelyDuplicateNames(names[i]!, names[j]!)) continue;
      found.set(i, [...(found.get(i) ?? []), j]);
    }
  }
  return found;
}
